import React, { useContext, useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HelpIcon from "@mui/icons-material/HelpOutlineOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, Grid, Typography } from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";

import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";
import {
  AddUserToGroupData,
  BulkAddUsersToGroupsRequest,
  Region,
} from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/workflows_pb";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import mainStyles from "../../styles";
import RegionSelector from "./shared/region-selector";
import { bulkAddUsersToGroups } from "api/endpoints/workflows";
import { callOptionsHeaders } from "api/helpers";
import ActionPermissionWrapper from "components/shared/action-permission-wrapper";
import CodeBox from "components/shared/code-box";
import DragAndDrop, { FileToReturn } from "components/shared/drag-and-drop";
import { GrpcError, SnackbarContext } from "context/snackbar-context";
import { parse } from "csv-parse/browser/esm";

interface BulkAddUsersToGroupsFormProps {
  listSupportWorkflows: () => void;
}

const BulkAddUsersToGroupsForm = ({
  listSupportWorkflows,
}: BulkAddUsersToGroupsFormProps) => {
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [formState, setFormState] = useState<FileToReturn[]>([]);

  const { showError } = useContext(SnackbarContext);
  const [targetRegion, setTargetRegion] = useState<Region>();

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result as string);
      };
      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };
      reader.readAsText(file);
    });
  };

  const processCSVFiles = async (): Promise<AddUserToGroupData[]> => {
    const csvFiles = formState.filter((file) =>
      file.type.startsWith("text/csv"),
    );
    const usersToAdd: AddUserToGroupData[] = [];

    const promises = csvFiles.map(async (file) => {
      const text = await readFileAsText(file);

      return new Promise<void>((resolve, reject) => {
        parse(
          text,
          { skip_empty_lines: true, trim: true },
          (err, output: string[][]) => {
            if (err || output.length === 0 || output[0].length !== 3) {
              let displayError: Error;
              if (err) {
                displayError = new Error("Error parsing CSV:", err);
              } else if (output.length === 0) {
                displayError = new Error("No data found in CSV");
              } else if (output[0].length !== 3) {
                displayError = new Error(
                  `Invalid number of columns [${output[0].length}] csv must be in the format: user ID,target organization ID,target group ID. See the info panel for more details.`,
                );
              } else {
                displayError = new Error("Unknown error occurred");
              }
              showError(displayError as GrpcError);
              reject(displayError);
              return;
            }

            if (file.csvHeaders) {
              output.shift(); // Remove headers if necessary
            }

            output.forEach((row) => {
              usersToAdd.push({
                userId: row[0],
                targetOrgId: row[1],
                targetGroupId: row[2],
              } as AddUserToGroupData);
            });

            resolve();
          },
        );
      });
    });

    await Promise.all(promises);

    return usersToAdd;
  };

  const addUsersToGroups = async (region: Region) => {
    setIsLoading(true);

    processCSVFiles().then((usersToAdd) => {
      const req = {
        usersToAdd,
        region: region,
      } as BulkAddUsersToGroupsRequest;

      bulkAddUsersToGroups(req, callOptionsHeaders(DEFAULT_REGION_CODE))
        .then(() => {
          setFormState([]);
        })
        .then(() => {
          listSupportWorkflows();
        })
        .catch((error) => {
          showError(error as GrpcError);
        })
        .finally(() => {
          setIsLoading(false);
        });
    });
  };

  const sampleCSV = `  user_id,target_org_id,target_group_id
  cc09330b-dbde-4806-9a8f-552fbd892db4,5b367bc6-bee0-4129-a520-c6b18343b6da,a5975f3d-695c-41b4-a871-14617db36dd4`;

  const BulkAddUsersToGroupsInfoCard = () => {
    return (
      <Grid item xs={12}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="info-panel-content"
            id="info-panel-header"
            test-dataid="info-panel-summary"
          >
            <HelpIcon color="primary" />
            <Typography
              data-testid="info-panel-title"
              style={{ paddingLeft: ".25em" }}
            >
              How to use: Bulk Add Users to Groups
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography
              style={{ textAlign: "left" }}
              data-testid="info-panel-description"
            >
              <p>
                Bulk Add Users to Groups will take in a CSV of this row format:
              </p>
              <CodeBox codeBlob={sampleCSV} />
              <ul>
                <li>
                  Every organization id should be identical since all users
                  should be migrating to the same organization.
                </li>
                <li>
                  Every group id should belong to that target organization.
                </li>
              </ul>
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Grid>
    );
  };

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <Box sx={{ width: "100%" }}>
            <Typography variant="h6">
              Upload CSV File with User and Group Details
            </Typography>
            <DragAndDrop
              acceptedFileTypes={{ "text/csv": [".csv"] }}
              setDragAndDropFiles={setFormState}
              maxAcceptedFiles={1}
              files={formState}
              setFiles={setFormState}
              removeFilePreview={(index: number) => {
                setFormState((prev) => prev.filter((_, i) => i !== index));
              }}
            />
          </Box>
          <RegionSelector
            targetRegion={targetRegion}
            setTargetRegion={setTargetRegion}
            isLoading={isLoading}
          />
          <ActionPermissionWrapper action={ActionType.BULK_ADD_USERS_TO_GROUPS}>
            <LoadingButton
              variant="contained"
              color="primary"
              size="large"
              onClick={() => {
                addUsersToGroups(targetRegion as Region);
                setFormState([]);
              }}
              loading={isLoading}
              disabled={formState.length === 0 || targetRegion === undefined}
              data-testid="execute-workflow-button"
            >
              Execute Workflow
            </LoadingButton>
          </ActionPermissionWrapper>
        </Box>
      </Grid>

      <Grid item xs={12} style={{ paddingBottom: "20px" }}>
        <Grid container alignContent="space-between" alignItems="center">
          <BulkAddUsersToGroupsInfoCard />
        </Grid>
      </Grid>
    </>
  );
};

export default BulkAddUsersToGroupsForm;
